import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import clx from "classnames";
import CalendarBody from '../CalendarBody/CalendarBody';
import { BetweenChangeType, SelectDateType, MAX_YEAR, OLD_YEAR } from '..';
import { isValidDate } from '../utils';
import CalendarControl from '../CalendarControl/CalendarControl';
import st from "./CalendarWidget.module.scss";
import CalendarInputs from '../CalendarInput/CalendarInputs';

interface ICalendarWidgetProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	
	/** Только для чтения */
	readOnly?: boolean;
	/** Минимальная дата доступная */
	minDate?: Date;
	/** Максимальная дата доступная */
	maxDate?: Date;

	/** Тип календаря */
	selectType?: SelectDateType;

	/** Отображать ли заголовок */
	headless?: boolean;
	/** Отображать ли кнопки управления календарем */
	isControlWidget?: boolean;

	/** Выбранные даты */
	values: Array<Date|null>;
	/** Клик по дню календаря */
	onDateClick?: ( value: Date ) => void;
	/** Вызывается при изменении выбранных дат */
	onValuesChange?: ( values: Array<Date|null>, date: Date | null, index: number ) => void;

	/** Функция-рендер нижнего тулбара */
	renderBottomToolbar?: () => ReactNode;

	/** Вызывается при клике на крестик в шапке виджета ( при включенном отображении ) */
	onClose?: () => void;
};

/** Компонента отображающая виджет календаря */
const CalendarWidget: FC<ICalendarWidgetProps> = ( props ) => {

	const {
		readOnly = false, isControlWidget = true, headless = true, selectType = "once",
		values, onDateClick, onClose, onValuesChange, renderBottomToolbar,
		minDate = new Date(OLD_YEAR, 0, 1),
		maxDate = new Date(MAX_YEAR, 11, 31),
		className, ...otherProps
	} = props;

	const now: Date = useMemo( () => {
		const tmp = new Date( Date.now() );
		return new Date( tmp.getFullYear(), tmp.getMonth(), tmp.getDate(), 0, 0, 0, 0 );
	}, [] );

	// Месяц
	const [ month, setMonth ] = useState<number>(now.getMonth());
	// Год
	const [ year, setYear ] = useState<number>(now.getFullYear());
	// Индекс текущей даты
	const [ currentIndex, setCurrentIndex ] = useState<number>(0);

	useEffect( () => {
		let date: Date;
		if ( isValidDate( values[currentIndex] ) )
			date = values[currentIndex]!;
		else if ( isValidDate( values[0] ) )
			date = values[0]!;
		else 
			date = now;
		
		if ( isValidDate( date ) ) {
			setMonth( date.getMonth() );
			setYear( date.getFullYear() )
		}
	}, [ currentIndex, values ] );

	const getBetweenChangeType = () => {
		if ( selectType === "between" ) {
			return currentIndex === 0 ? "changeForm" : "changeTo";
		}
		else 
			return "none";
	};

	/** Рендер шапки  */
	const renderHead = () => (
		<div className={st.widget__header}>
			<h4>Календарь</h4>
			<button 
				type="button"
				onClick={onClose}
			>
				<svg width="32" height="32" viewBox="0 0 32 32" fill="inherit">
					<path d="M22.295 11.115C22.6844 10.7256 22.6844 10.0944 22.295 9.705C21.9056 9.31564 21.2744 9.31564 20.885 9.705L16 14.59L11.115 9.705C10.7256 9.31564 10.0944 9.31564 9.705 9.705C9.31564 10.0944 9.31564 10.7256 9.705 11.115L14.59 16L9.705 20.885C9.31564 21.2744 9.31564 21.9056 9.705 22.295C10.0944 22.6844 10.7256 22.6844 11.115 22.295L16 17.41L20.885 22.295C21.2744 22.6844 21.9056 22.6844 22.295 22.295C22.6844 21.9056 22.6844 21.2744 22.295 20.885L17.41 16L22.295 11.115Z" fill="inherit"></path>
				</svg>
			</button>
		</div>
	);

	/** Тут логика изменения выбранных значений и текущего индекса */
	const onClickDataHandler = ( date: Date | null, index: number = -1 ) => {
		if ( !!onDateClick && !!date ) {
			onDateClick( date );
		}
		let newList: Array<Date|null> = [ ...values ];
		if ( selectType === "once" ) {
			if ( date ) {
				setMonth( date.getMonth() );
				setYear( date.getFullYear() )
			}
			newList = [ date ];
		}
		else if ( selectType === "between" ) {
			const _index = index !== -1 ? index : currentIndex;
			const nextIndex = index !== -1 ? index : ( currentIndex === 0 ? 1 : 0 );
			if ( _index === 0 ) {
				newList[0] = date;
			}
			else if ( _index === 1 ) {
				newList[1] = date;
			}
			setCurrentIndex(nextIndex);
		}
		else if ( selectType === "list" ) {
			newList[currentIndex] = date;
		}

		if ( onValuesChange ) {
			onValuesChange( newList, date, index );
		}
	};

	const onChangeValueByTypes = ( value: Date | null, isFirst: boolean = true ) => onClickDataHandler( value, ( isFirst ? 0 : 1 ) );

	const commonProps = { readOnly, minDate, maxDate, month, year };
	return (
		<div className={clx( st.widget, className)} {...otherProps}>
			{ !headless && renderHead() }
			{ 
				selectType === "between" && 
				<CalendarInputs
					readOnly={readOnly}
					firstValue={values[0]}
					lastValue={values[1]}
					isFirst={currentIndex===0}
					onChangeValue={onChangeValueByTypes}
					onChangeBetweenChangeType={setCurrentIndex}
				/>
			}
			{ 
				isControlWidget && 
				<CalendarControl
					{...commonProps}
					setMonth={setMonth}
					setYear={setYear}
				/>
			}
			<CalendarBody
				{...commonProps}
				betweenChangeType={getBetweenChangeType()}
				selectedDates={values}
				onDateClick={onClickDataHandler}
			/>
			{ !!renderBottomToolbar && renderBottomToolbar() }
		</div>
	);
};

export default CalendarWidget;
